import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AuthShell from '../components/AuthShell.tsx';
import { accountApi, acceptAuth } from '../lib/accountApi.ts';
import { useDocumentTitle } from '../lib/useDocumentTitle.ts';

type State = 'checking' | 'done' | 'failed';

/**
 * The far end of the confirmation email. Following the link signs you in right
 * here, in whatever browser opened it - which is usually not the tab that started
 * the signup, and is the whole reason the login is a token rather than a cookie.
 */
export default function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<State>('checking');
  // React runs effects twice in development, and this token is single-use.
  const claimed = useRef(false);
  useDocumentTitle('Confirm your address');

  const token = params.get('token') ?? '';

  useEffect(() => {
    if (!token) {
      setState('failed');
      return;
    }
    if (claimed.current) return;
    claimed.current = true;

    accountApi
      .verify(token)
      .then((result) => {
        acceptAuth(result);
        setState('done');
        navigate('/dashboard', { replace: true });
      })
      .catch(() => setState('failed'));
  }, [token, navigate]);

  if (state === 'failed') {
    return (
      <AuthShell
        eyebrow="That link is spent"
        title="It expired, or it was already used"
        intro="Confirmation links only work once, and not for long. Signing in will offer you a fresh one."
      >
        <Link to="/signin" className="btn btn-primary justify-self-start">
          Go to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="One moment"
      title={state === 'done' ? 'You are in' : 'Confirming your address…'}
    />
  );
}
